/**
 * SVC-TYPE-03 — admin for the service-type catalog and the fee schedule. This is
 * where the list Seth approved lives editably: add/rename/retire service types,
 * and edit the diagnostic fees (the live version of Diagnostic Fees 12-02-2024 —
 * the PDF becomes a table). Writes gated on settings.manage; read-only otherwise.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Check, X, Trash2, ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/cn";
import { InfoTip } from "@/components/InfoTip";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { systemsForType, type CallSystem } from "@/features/team/skillSteer";
import {
  useAllServiceTypes,
  useSaveServiceType,
  useAddServiceType,
  useFeeItems,
  useSaveFeeItem,
  useAddFeeItem,
  usePlanDiscounts,
  useSavePlanDiscount,
  useAddPlanDiscount,
  useDeletePlanDiscount,
  categoryLabel,
  CATEGORIES,
  FEE_CODES,
  type ServiceType,
  type FeeItem,
  type PlanDiscount,
} from "@/features/customer-360/serviceTypes";

const URGENCY_CHIP: Record<string, string> = {
  asap: "bg-red-50 text-red-700",
  soon: "bg-amber-50 text-amber-700",
  routine: "bg-slate-100 text-slate-600",
};

export function ServiceTypesSection() {
  const canManage = useCan("settings.scheduling");
  const { data: types } = useAllServiceTypes();
  const add = useAddServiceType();
  const [editId, setEditId] = useState<string | null>(null);

  const grouped = useMemo(() => {
    const m = new Map<string, ServiceType[]>();
    for (const t of types ?? []) m.set(t.category, [...(m.get(t.category) ?? []), t]);
    return [...m.entries()];
  }, [types]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        {canManage && (
          <button
            type="button"
            onClick={() => add.mutate()}
            className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-2.5 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800"
          >
            <Plus className="h-3.5 w-3.5" /> Add type
          </button>
        )}
      </div>

      {grouped.map(([cat, list]) => (
        <div key={cat} className="rounded-xl border border-hairline bg-white p-4 shadow-e1">
          <div className="mb-2 text-[13px] font-bold text-slate-800">
            {categoryLabel(cat)}
          </div>
          <ul className="divide-y divide-hairline">
            {list.map((t) =>
              editId === t.id ? (
                <TypeEditRow key={t.id} t={t} onDone={() => setEditId(null)} />
              ) : (
                <TypeRow
                  key={t.id}
                  t={t}
                  canManage={canManage}
                  onEdit={() => setEditId(t.id)}
                />
              ),
            )}
          </ul>
        </div>
      ))}
    </div>
  );
}

function TypeRow({
  t,
  canManage,
  onEdit,
}: {
  t: ServiceType;
  canManage: boolean;
  onEdit: () => void;
}) {
  const save = useSaveServiceType();
  return (
    <li className={cn("flex items-center gap-3 py-2", !t.active && "opacity-50")}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-800">{t.name}</span>
          {!t.active && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-500">Off</span>}
          <span
            className={cn(
              "rounded-full px-1.5 py-0.5 text-[11px] font-semibold",
              URGENCY_CHIP[t.default_urgency],
            )}
          >
            {t.default_urgency}
          </span>
          <span className="text-[11px] text-slate-400">{t.est_minutes}m</span>
          {t.fee_code && (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-500">
              {t.fee_code}
            </span>
          )}
          {(t.checklist?.length ?? 0) > 0 && (
            <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700">
              ✓ {t.checklist!.length}-item checklist
            </span>
          )}
        </div>
        {t.default_description && (
          <div className="truncate text-[11px] text-slate-400">{t.default_description}</div>
        )}
      </div>
      {canManage && (
        <>
          <button
            type="button"
            onClick={() => save.mutate({ id: t.id, active: !t.active })}
            className={cn(
              "rounded-md px-2 py-0.5 text-[11px] font-medium transition-colors",
              t.active
                ? "text-slate-500 hover:bg-slate-100"
                : "text-emerald-700 hover:bg-emerald-50",
            )}
          >
            {t.active ? "Turn off" : "Turn on"}
          </button>
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
          >
            <Pencil className="h-3 w-3" /> Edit
          </button>
        </>
      )}
    </li>
  );
}

function TypeEditRow({ t, onDone }: { t: ServiceType; onDone: () => void }) {
  const save = useSaveServiceType();
  const [name, setName] = useState(t.name);
  const [desc, setDesc] = useState(t.default_description ?? "");
  const [urgency, setUrgency] = useState(t.default_urgency);
  const [minutes, setMinutes] = useState(String(t.est_minutes));
  const [category, setCategory] = useState(t.category);
  const [feeCode, setFeeCode] = useState(t.fee_code ?? "");
  // SETTINGS-NAV-02: which equipment a call is about — explicit now (it used
  // to be guessed from the name + loose tags). Drives the equipment steering
  // in the booking grid (Skills & Levels). None = not about a unit on file.
  const [about, setAbout] = useState<Set<CallSystem>>(() => new Set(systemsForType(t)));
  const toggleAbout = (k: CallSystem) => setAbout((cur) => { const n = new Set(cur); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const [checklist, setChecklist] = useState((t.checklist ?? []).join("\n"));

  const commit = () =>
    save.mutate(
      {
        id: t.id,
        name: name.trim() || t.name,
        default_description: desc.trim() || null,
        default_urgency: urgency,
        est_minutes: parseInt(minutes, 10) || 60,
        category,
        fee_code: feeCode || null,
        skills: about.size ? (["heat", "cool", "water"] as CallSystem[]).filter((k) => about.has(k)) : ["none"],
        checklist: (() => {
          const items = checklist.split("\n").map((s) => s.trim()).filter(Boolean);
          return items.length ? items : null;
        })(),
      },
      { onSuccess: onDone },
    );

  return (
    <li className="space-y-2 py-3">
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="w-full rounded-md border border-brand-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30"
      />
      <input
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        placeholder="Default description (autofills the note)"
        className="w-full rounded-md border border-hairline px-2 py-1.5 text-xs"
      />
      <div className="flex flex-wrap gap-2">
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="rounded-md border border-hairline px-2 py-1 text-xs">
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>{categoryLabel(c)}</option>
          ))}
        </select>
        <select value={urgency} onChange={(e) => setUrgency(e.target.value as ServiceType["default_urgency"])} className="rounded-md border border-hairline px-2 py-1 text-xs">
          <option value="asap">ASAP</option>
          <option value="soon">Soon</option>
          <option value="routine">Routine</option>
        </select>
        <label className="inline-flex items-center gap-1 text-xs text-slate-500">
          <input type="number" value={minutes} onChange={(e) => setMinutes(e.target.value)} className="w-16 rounded-md border border-hairline px-2 py-1 text-xs" /> min
        </label>
        <select value={feeCode} onChange={(e) => setFeeCode(e.target.value)} className="rounded-md border border-hairline px-2 py-1 text-xs">
          <option value="">No fee (covered / estimate / COD)</option>
          {FEE_CODES.map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <span className="mr-0.5 text-slate-500">About</span>
        {(([["heat", "Heating"], ["cool", "Cooling"], ["water", "Water heater"]]) as [CallSystem, string][]).map(([k, label]) => (
          <button key={k} type="button" aria-pressed={about.has(k)} onClick={() => toggleAbout(k)}
            className={cn("rounded-full border px-2.5 py-0.5 text-[12px] font-semibold transition-colors",
              about.has(k) ? "border-brand-600 bg-brand-600 text-white" : "border-hairline bg-white text-slate-600 hover:bg-slate-50")}>
            {label}
          </button>
        ))}
        <InfoTip label="How this is used">
          The equipment on file for these helps pick the tech. Pick none for calls that aren&apos;t about a unit on file, like
          installs, estimates and add-ons.
        </InfoTip>
        <Link to="/settings?section=skills" className="ml-auto inline-flex items-center gap-0.5 text-[12px] font-semibold text-brand-700 hover:text-brand-900">
          Who can take it <ArrowUpRight className="h-3 w-3" />
        </Link>
      </div>
      <div>
        <div className="mb-1 flex items-center gap-1.5">
          <label htmlFor={`type-checklist-${t.id}`} className="text-[11px] font-semibold text-slate-500">
            Checklist for this type — one item per line (blank = no checklist)
          </label>
          <InfoTip label="Where the checklist shows">
            The truck shows these on every call booked with this type. Ticked items print on the customer&apos;s invoice.
          </InfoTip>
        </div>
        <textarea
          id={`type-checklist-${t.id}`}
          value={checklist}
          onChange={(e) => setChecklist(e.target.value)}
          rows={Math.min(12, Math.max(4, checklist.split("\n").length + 1))}
          placeholder={"Filter checked / replaced\nRefrigerant pressures checked\n…"}
          className="w-full rounded-md border border-hairline px-2 py-1.5 font-mono text-[11.5px] leading-relaxed focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
        />
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onDone} className="inline-flex items-center gap-1 rounded-md border border-hairline px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-50">
          <X className="h-3 w-3" /> Cancel
        </button>
        <button type="button" onClick={commit} disabled={save.isPending} className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-2.5 py-1 text-xs font-semibold text-white hover:bg-brand-800 disabled:bg-brand-300">
          <Check className="h-3 w-3" /> {save.isPending ? "Saving…" : "Save"}
        </button>
      </div>
    </li>
  );
}

const SECTION_LABEL: Record<string, string> = {
  residential_diag: "Residential — diagnostic",
  commercial_diag: "Commercial — diagnostic",
  demand_tuneup: "Demand tune-ups",
  specialty: "Specialty",
  tm: "Time & material",
  refrigerant: "Refrigerant",
  nest: "Nest thermostat",
};

export function PricingSection() {
  const canManage = useCan("settings.scheduling");
  const { data: items } = useFeeItems();

  const grouped = useMemo(() => {
    const m = new Map<string, FeeItem[]>();
    for (const f of items ?? []) m.set(f.section, [...(m.get(f.section) ?? []), f]);
    return [...m.entries()];
  }, [items]);

  return (
    <div className="space-y-4">

      <MembershipDiscounts canManage={canManage} />

      {canManage && <AddFeeForm />}

      {grouped.map(([section, list]) => (
        <div key={section} className="rounded-xl border border-hairline bg-white p-4 shadow-e1">
          <div className="mb-2 text-[13px] font-bold text-slate-800">
            {SECTION_LABEL[section] ?? section}
          </div>
          <ul className="divide-y divide-hairline">
            {list.map((f) => (
              <FeeRow key={f.id} f={f} canManage={canManage} />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function FeeRow({ f, canManage }: { f: FeeItem; canManage: boolean }) {
  const save = useSaveFeeItem();
  const [amount, setAmount] = useState(String(f.amount));
  const dirty = amount !== String(f.amount);
  const mods = [
    f.plan_scope === "plus" ? "PLUS" : null,
    f.segment === "commercial" ? "commercial" : null,
    f.zone === "downtown" ? "downtown" : null,
    f.after_hours ? "after-hours" : null,
  ].filter(Boolean);

  return (
    <li className={cn("flex items-center gap-3 py-2", !f.active && "opacity-50")}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-800">{f.label}</span>
          {mods.map((m) => (
            <span key={m} className="rounded-full bg-violet-50 px-1.5 py-0.5 text-[11px] font-medium text-violet-700">
              {m}
            </span>
          ))}
        </div>
        {f.note && <div className="text-[11px] text-slate-400">{f.note}</div>}
      </div>
      <div className="flex items-center gap-1">
        <span className="text-slate-400">$</span>
        <input
          type="number"
          value={amount}
          disabled={!canManage}
          onChange={(e) => setAmount(e.target.value)}
          className="w-20 rounded-md border border-hairline px-2 py-1 text-right text-sm text-slate-800 disabled:bg-slate-50"
        />
        {f.unit === "per_hour" && <span className="text-[11px] text-slate-400">/hr</span>}
        {f.unit === "per_lb" && <span className="text-[11px] text-slate-400">/lb</span>}
        {canManage && dirty && (
          <button
            type="button"
            // SETTINGS-AUDIT-01: an emptied box must NOT silently save $0.
            onClick={() => { if (amount.trim() === "" || Number.isNaN(Number(amount))) { setAmount(f.amount == null ? "" : String(f.amount)); return; } save.mutate({ id: f.id, amount: Number(amount) }); }}
            disabled={save.isPending}
            className="ml-1 rounded-md bg-brand-700 px-2 py-1 text-[11px] font-semibold text-white hover:bg-brand-800 disabled:bg-brand-300"
          >
            Save
          </button>
        )}
        {save.isError && <span className="ml-1 text-[10px] font-medium text-rose-600">didn&apos;t save</span>}
      </div>
    </li>
  );
}

function MembershipDiscounts({ canManage }: { canManage: boolean }) {
  const { data: tiers } = usePlanDiscounts();
  const add = useAddPlanDiscount();
  if (!tiers) return null;
  return (
    <div className="rounded-xl border border-hairline bg-white p-4 shadow-e1">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[13px] font-bold text-slate-800">
          Member Repair Discount
          <InfoTip label="How the repair discount works">
            Repair discount by plan length, shown to the customer as savings on the estimate. A tier covers plans of that length
            or longer, up to the next tier; the diagnostic discount is the PLUS rate in the fees below.
          </InfoTip>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => add.mutate()}
            className="inline-flex items-center gap-1 rounded-md border border-dashed border-slate-300 px-1.5 py-0.5 text-[11px] font-medium text-slate-500 hover:bg-slate-50"
          >
            <Plus className="h-3 w-3" /> Add tier
          </button>
        )}
      </div>
      <ul className="divide-y divide-hairline">
        {tiers.map((t) => (
          <PlanDiscountRow key={t.id} t={t} canManage={canManage} />
        ))}
      </ul>
    </div>
  );
}

function PlanDiscountRow({
  t,
  canManage,
}: {
  t: PlanDiscount;
  canManage: boolean;
}) {
  const save = useSavePlanDiscount();
  const del = useDeletePlanDiscount();
  const [confirmDel, setConfirmDel] = useState(false);
  const [label, setLabel] = useState(t.label);
  const [minY, setMinY] = useState(String(t.min_years));
  const [pct, setPct] = useState(String(t.repair_pct));
  const dirty =
    label !== t.label ||
    minY !== String(t.min_years) ||
    pct !== String(t.repair_pct);
  return (
    <li className="flex items-center gap-2 py-2">
      <input
        value={label}
        disabled={!canManage}
        onChange={(e) => setLabel(e.target.value)}
        className="min-w-0 flex-1 rounded-md border border-hairline px-2 py-1 text-sm text-slate-800 disabled:bg-slate-50"
      />
      <span className="text-[11px] text-slate-400">≥</span>
      <input
        type="number"
        value={minY}
        disabled={!canManage}
        onChange={(e) => setMinY(e.target.value)}
        className="w-12 rounded-md border border-hairline px-1.5 py-1 text-center text-sm disabled:bg-slate-50"
      />
      <span className="text-[11px] text-slate-400">yr</span>
      <input
        type="number"
        value={pct}
        disabled={!canManage}
        onChange={(e) => setPct(e.target.value)}
        className="w-14 rounded-md border border-hairline px-2 py-1 text-right text-sm disabled:bg-slate-50"
      />
      <span className="text-[11px] text-slate-400">% off</span>
      {canManage && dirty && (
        <button
          type="button"
          onClick={() =>
            save.mutate({
              id: t.id,
              label: label.trim() || t.label,
              min_years: Number(minY) || 1,
              repair_pct: Number(pct),
            })
          }
          disabled={save.isPending}
          className="rounded-md bg-brand-700 px-2 py-1 text-[11px] font-semibold text-white hover:bg-brand-800 disabled:bg-brand-300"
        >
          Save
        </button>
      )}
      {canManage && (confirmDel ? (
        // SETTINGS-AUDIT-01: hard delete needs a second click, not a twitch.
        <span className="inline-flex items-center gap-1">
          <button type="button" onClick={() => del.mutate(t.id)} disabled={del.isPending}
            className="rounded-md bg-rose-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-rose-700 disabled:opacity-50">
            Delete tier?
          </button>
          <button type="button" onClick={() => setConfirmDel(false)} className="text-[11px] text-slate-400 hover:text-slate-600">cancel</button>
        </span>
      ) : (
        <button
          type="button"
          onClick={() => setConfirmDel(true)}
          title="Delete tier"
          className="rounded-md p-1 text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-600"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      ))}
      {del.isError && <span className="text-[10px] font-medium text-rose-600">delete failed</span>}
    </li>
  );
}

function AddFeeForm() {
  const add = useAddFeeItem();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [feeCode, setFeeCode] = useState(FEE_CODES[0]);
  const [section, setSection] = useState("residential_diag");
  const [amount, setAmount] = useState("");
  const [segment, setSegment] = useState("residential");
  const [planScope, setPlanScope] = useState("standard");
  const [zone, setZone] = useState("any");
  const [unit, setUnit] = useState("flat");
  const [afterHours, setAfterHours] = useState(false);
  const canSave = label.trim() !== "" && amount.trim() !== "" && Number(amount) >= 0 && !Number.isNaN(Number(amount));

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 rounded-md border border-dashed border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-50"
      >
        <Plus className="h-3.5 w-3.5" /> Add fee
      </button>
    );
  }
  return (
    <div className="space-y-2 rounded-xl border border-brand-200 bg-white p-4 shadow-e1">
      <div className="text-[13px] font-bold text-slate-800">
        New Fee
      </div>
      <input
        placeholder="Label (e.g. Boiler diagnostic — downtown)"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm"
      />
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <select value={feeCode} onChange={(e) => setFeeCode(e.target.value)} className="rounded-md border border-hairline px-2 py-1">
          {FEE_CODES.map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
        <select value={section} onChange={(e) => setSection(e.target.value)} className="rounded-md border border-hairline px-2 py-1">
          {Object.keys(SECTION_LABEL).map((s) => (
            <option key={s} value={s}>{SECTION_LABEL[s]}</option>
          ))}
        </select>
        <label className="inline-flex items-center gap-1 text-slate-500">
          $
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-20 rounded-md border border-hairline px-2 py-1 text-right" />
        </label>
        <select value={unit} onChange={(e) => setUnit(e.target.value)} title="How the amount charges" className="rounded-md border border-hairline px-2 py-1">
          <option value="flat">flat</option>
          <option value="per_hour">per hour</option>
          <option value="per_lb">per lb</option>
        </select>
        <select value={segment} onChange={(e) => setSegment(e.target.value)} className="rounded-md border border-hairline px-2 py-1">
          <option value="residential">residential</option>
          <option value="commercial">commercial</option>
          <option value="any">any segment</option>
        </select>
        <select value={planScope} onChange={(e) => setPlanScope(e.target.value)} className="rounded-md border border-hairline px-2 py-1">
          <option value="standard">standard</option>
          <option value="plus">PLUS</option>
          <option value="any">any plan</option>
        </select>
        <select value={zone} onChange={(e) => setZone(e.target.value)} className="rounded-md border border-hairline px-2 py-1">
          <option value="any">any zone</option>
          <option value="downtown">downtown</option>
        </select>
        <label className="inline-flex items-center gap-1 text-slate-500">
          <input type="checkbox" checked={afterHours} onChange={(e) => setAfterHours(e.target.checked)} /> after-hours
        </label>
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={() => setOpen(false)} className="rounded-md border border-hairline px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-50">
          Cancel
        </button>
        <button
          type="button"
          disabled={!canSave || add.isPending}
          onClick={() =>
            add.mutate(
              {
                fee_code: feeCode,
                label: label.trim(),
                section,
                amount: Number(amount),
                unit,
                segment,
                plan_scope: planScope,
                zone,
                after_hours: afterHours,
              },
              {
                onSuccess: () => {
                  setOpen(false);
                  setLabel("");
                  setAmount("");
                },
              },
            )
          }
          className="rounded-md bg-brand-700 px-2.5 py-1 text-xs font-semibold text-white hover:bg-brand-800 disabled:bg-brand-300"
        >
          {add.isPending ? "Adding…" : "Add fee"}
        </button>
      </div>
      {add.isError && <p className="text-right text-[11px] font-medium text-rose-600">Couldn&apos;t add that fee — try again.</p>}
    </div>
  );
}
